import dayjs, { Dayjs } from 'dayjs';
import { TimeFormatType, TaskItemDuration, DateTimeSet, TaskItem } from '@demind-inc/core';

export const formatTime = (timeFormat: TimeFormatType) => {
  return timeFormat === '12h' ? 'h:mm A' : 'HH:mm';
};

export const formatTimeFromDayjs = (
  time: Dayjs,
  timeFormat: TimeFormatType,
  removeSpace: boolean = false
) => {
  if (timeFormat === '24h') {
    return time.format('HH:mm');
  }
  return removeSpace ? time.format('h:mmA') : time.format('h:mm A');
};

export const isHHMMFormat = (time: string) => {
  return time?.match(/^\d{2}:\d{2}$/);
};

export const formatTaskTimeRange = ({
  startDatetime,
  endDatetime,
  timeFormat,
}: {
  startDatetime?: Dayjs;
  endDatetime?: Dayjs;
  timeFormat: TimeFormatType;
}) => {
  const format = timeFormat === '12h' ? 'h:mmA' : 'HH:mm';

  const formatTime = (dateTime: Dayjs) => {
    return dateTime ? dateTime.format(format) : '';
  };

  const startTime = formatTime(startDatetime);
  const endTime = formatTime(endDatetime);

  if (!startTime || !endTime) {
    return null;
  }

  return `${startTime}-${endTime}`;
};

export const formatTaskTimeRangeFromDuration = ({
  duration,
  dueDateTime,
  timeFormat,
}: {
  duration: TaskItemDuration;
  dueDateTime: DateTimeSet;
  timeFormat: TimeFormatType;
}) => {
  const endDateTime =
    dueDateTime?.datetime && duration.amount
      ? dayjs(dueDateTime.datetime).add(duration.amount, duration.unit)
      : undefined;
  return formatTaskTimeRange({
    startDatetime: dueDateTime?.datetime ? dayjs(dueDateTime.datetime) : undefined,
    endDatetime: endDateTime,
    timeFormat,
  });
};

export const formatTimeFromHHMM = (
  timeString: string,
  timeFormat: TimeFormatType,
  removeSpace: boolean = false
) => {
  if (timeFormat === '24h') {
    return timeString;
  }
  const newDayjs = dayjs(`${dayjs().format('YYYY-MM-DD')}T${timeString}`);

  return removeSpace ? newDayjs.format('h:mmA') : newDayjs.format('h:mm A');
};

// Generate time options with specified interval
export const generateTimeOptions = (interval: number = 15, timeFormat: TimeFormatType = '24h') => {
  const times: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += interval) {
      const time = dayjs().hour(h).minute(m);
      times.push(formatTimeFromDayjs(time, timeFormat));
    }
  }
  return times;
};

export const isValidTimeFormat = (time: string, timeFormat: TimeFormatType): boolean => {
  if (!time || typeof time !== 'string') return false;

  const trimmedTime = time.trim();
  if (!trimmedTime) return false;

  try {
    const format = timeFormat === '12h' ? 'h:mm A' : 'HH:mm';
    const parsed = dayjs(trimmedTime, format, true);
    return parsed.isValid();
  } catch (error) {
    return false;
  }
};

export const parseTimeString = (timeStr: string, timeFormat: TimeFormatType): Dayjs | null => {
  if (!timeStr || !isValidTimeFormat(timeStr, timeFormat)) {
    return null;
  }

  const today = dayjs().format('YYYY-MM-DD');
  const format = timeFormat === '12h' ? 'YYYY-MM-DD h:mm A' : 'YYYY-MM-DD HH:mm';
  try {
    const parsed = dayjs(`${today} ${timeStr.trim()}`, format, true);

    return parsed.isValid() ? parsed : null;
  } catch (error) {
    console.error('Error parsing time:', error);
    return null;
  }
};

/**
 * Build exact suggestions based on a fully-typed hh:mm.
 * - In 12h mode: returns ["h:mm AM", "h:mm PM"] if hour 1–12 and minute 00–59
 * - In 24h mode: returns ["HH:mm"] (zero-padded) if hour 00–23 and minute 00–59
 */
export const buildUserTypedOptions = (iv: string, is12h: boolean): string[] => {
  const m = iv.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return [];
  let h = Number(m[1]);
  const mm = Number(m[2]);

  if (Number.isNaN(h) || Number.isNaN(mm) || mm < 0 || mm > 59) return [];

  if (is12h) {
    if (h < 1 || h > 12) return [];
    return [`${h}:${m[2]} AM`, `${h}:${m[2]} PM`];
  }

  if (h < 0 || h > 23) return [];
  const hh = String(h).padStart(2, '0');
  return [`${hh}:${m[2]}`];
};

export const buildTaskTime = (task: TaskItem) => {
  const { duration, startDateTime, dueDateTime } = task;

  const end = dueDateTime.datetime ? dayjs(dueDateTime.datetime) : undefined;
  let start = startDateTime.datetime ? dayjs(startDateTime.datetime) : undefined;
  if (duration.amount && end) {
    start = end.subtract(duration.amount, duration.unit);
  }
  return {
    start: start ? start : undefined,
    end: end ? end : undefined,
  };
};
